import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useAuth } from '../auth';
import { StorageDropdown } from '../components/StorageDropdown';
import './admin-email.css';

type Template = { id: string; subject: string; html: string; text: string };
type Audience = {
  createdAfter: string | null;
  createdBefore: string | null;
  activity: string;
  activityDays: number;
  groups: string[];
  plans: string[];
  age: number | null;
  ageUnit: string;
  ageDirection: string;
  affiliate: string;
  emails: string[];
  excludeEmails: string[];
};
type Campaign = {
  id: string;
  subject: string;
  status: string;
  total: number;
  sent: number;
  skipped: number;
  uncertain: number;
  next_attempt_at: string | null;
};
const initial: Audience = {
  createdAfter: null,
  createdBefore: null,
  activity: 'any',
  activityDays: 30,
  groups: ['paid', 'free', 'other'],
  plans: [],
  age: null,
  ageUnit: 'days',
  ageDirection: 'newer',
  affiliate: 'all',
  emails: [],
  excludeEmails: [],
};
function EmailSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}) {
  return (
    <div className="email-select-field">
      <span>{label}</span>
      <StorageDropdown
        label={label}
        value={value}
        options={options}
        onChange={onChange}
        icon={null}
        className="email-select"
        menuWidth={280}
      />
    </div>
  );
}
const time = (value: string | null) => (value ? new Date(value).toLocaleString() : 'None');
function Preview({ html }: { html: string }) {
  return (
    <iframe
      title="Full email preview"
      sandbox=""
      referrerPolicy="no-referrer"
      srcDoc={`<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src https: data:; style-src 'unsafe-inline'; font-src https:;">${html}`}
    />
  );
}
export function AdminEmail() {
  const client = useQueryClient();
  const { user } = useAuth();
  const templates = useQuery({
    queryKey: ['email-templates'],
    queryFn: () =>
      api<{ templates: Template[]; plans: Array<{ code: string; name: string }> }>(
        '/admin/email/templates',
      ),
  });
  const [historyPage, setHistoryPage] = useState(0);
  const overview = useQuery({
    queryKey: ['email-campaigns', historyPage],
    queryFn: () =>
      api<{
        campaigns: Campaign[];
        campaignCount: number;
        used: number;
        deletions: Array<{
          user_id: string;
          status: string;
          attempts: number;
          last_error: string | null;
          next_attempt_at: string;
          completed_at: string | null;
        }>;
      }>(`/admin/email/campaigns?page=${historyPage}`),
    refetchInterval: 5000,
  });
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [draft, setDraft] = useState<Template | null>(null),
    [filters, setFilters] = useState(initial);
  const [audience, setAudience] = useState<{
    total: number;
    sample: Array<{ id: string; email: string }>;
  } | null>(null);
  const [notice, setNotice] = useState(''),
    [busy, setBusy] = useState(false),
    [confirm, setConfirm] = useState(false);
  const [view, setView] = useState<'compose' | 'history' | 'deletions'>('compose');
  const [detailTab, setDetailTab] = useState('preview');
  const heading = useRef<HTMLHeadingElement>(null);
  const [selected, setSelected] = useState<string | null>(null),
    [page, setPage] = useState(0);
  const detail = useQuery({
    queryKey: ['email-detail', selected, page],
    enabled: !!selected,
    queryFn: () =>
      api<{
        campaign: {
          id: string;
          subject: string;
          html: string;
          body_text: string;
          filters: Audience;
        };
        recipients: Array<{
          id: string;
          email: string;
          status: string;
          attempts: number;
          last_error: string | null;
          sent_at: string | null;
          next_attempt_at: string;
          provider_message_id: string | null;
        }>;
      }>(`/admin/email/campaigns/${selected}?page=${page}`),
    refetchInterval: 5000,
  });
  const change = (value: Partial<Audience>) => {
    setFilters({ ...filters, ...value });
    setAudience(null);
    setConfirm(false);
  };
  const action = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setNotice('');
    try {
      await fn();
      await client.invalidateQueries({ queryKey: ['email-campaigns'] });
      await client.invalidateQueries({ queryKey: ['email-detail'] });
    } catch (e) {
      setNotice(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (selected) {
      setDetailTab('preview');
      window.scrollTo({ top: 0 });
    }
  }, [selected]);
  useEffect(() => {
    if (selected && detail.data) heading.current?.focus();
  }, [selected, detail.data?.campaign.id]);
  if (selected)
    return (
      <div className="email-system email-detail-page">
        <button
          className="email-back"
          onClick={() => {
            setSelected(null);
            setView('history');
          }}
        >
          ← Campaign history
        </button>
        {notice && (
          <p role="status" className="settings-notice">
            {notice}
          </p>
        )}
        {detail.isLoading && <p role="status">Loading campaign…</p>}
        {detail.isError && (
          <p role="alert">
            Unable to load this campaign.{' '}
            <button onClick={() => void detail.refetch()}>Retry</button>
          </p>
        )}
        {detail.data && (
          <section className="email-campaign-detail">
            <h1 ref={heading} tabIndex={-1}>
              {detail.data.campaign.subject}
            </h1>
            <nav className="email-tabs" aria-label="Campaign detail">
              {[
                ['preview', 'Email preview'],
                ['recipients', 'Recipients'],
                ['source', 'HTML source'],
                ['audience', 'Audience'],
              ].map(([id, label]) => (
                <button
                  key={id}
                  aria-current={detailTab === id ? 'page' : undefined}
                  onClick={() => setDetailTab(id!)}
                >
                  {label}
                </button>
              ))}
            </nav>
            {detailTab === 'preview' && (
              <div className="email-saved-preview">
                <Preview html={detail.data.campaign.html} />
              </div>
            )}
            {detailTab === 'source' && (
              <div className="email-source">
                <label>
                  Saved HTML
                  <textarea readOnly value={detail.data.campaign.html} rows={24} />
                </label>
                <label>
                  Plain-text fallback
                  <textarea readOnly value={detail.data.campaign.body_text} rows={6} />
                </label>
              </div>
            )}
            {detailTab === 'audience' && (
              <dl className="email-audience-facts">
                {Object.entries(detail.data.campaign.filters).map(([key, value]) => (
                  <div key={key}>
                    <dt>
                      {(
                        {
                          groups: 'Account types',
                          plans: 'Plans',
                          age: 'Account age',
                          ageUnit: 'Age unit',
                          ageDirection: 'Age condition',
                          createdAfter: 'Created after',
                          createdBefore: 'Created before',
                          activity: 'Last sign-in',
                          activityDays: 'Activity window (days)',
                          affiliate: 'Affiliate status',
                          emails: 'Included addresses',
                          excludeEmails: 'Excluded addresses',
                        } as Record<string, string>
                      )[key] || key}
                    </dt>
                    <dd>
                      {Array.isArray(value)
                        ? value
                            .map(
                              (v) =>
                                (
                                  ({
                                    paid: 'Active subscriptions',
                                    free: 'Free accounts',
                                    other: 'Other active accounts',
                                  }) as Record<string, string>
                                )[v] || v,
                            )
                            .join(', ') || 'None specified'
                        : value === null
                          ? 'Any'
                          : String(value)}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
            <div hidden={detailTab !== 'recipients'}>
              <div className="email-deliveries">
                {detail.data.recipients.map((r) => (
                  <article key={r.id}>
                    <strong>
                      {r.email === 'deleted@invalid.bckt' ? 'Deleted account' : r.email}
                    </strong>
                    <span>
                      {r.status} · {r.attempts} attempts
                    </span>
                    <small>
                      {r.sent_at
                        ? `Accepted: ${time(r.sent_at)}`
                        : `Next attempt: ${time(r.next_attempt_at)}`}
                    </small>
                    {r.provider_message_id && (
                      <details className="email-provider-ref">
                        <summary>Provider reference</summary>
                        <code>{r.provider_message_id}</code>
                      </details>
                    )}
                    {r.last_error && <p>{r.last_error}</p>}
                    {r.status === 'uncertain' && (
                      <div>
                        {[
                          ['sent', 'Confirm provider accepted'],
                          ['retry', 'Retry (may duplicate)'],
                          ['skipped', 'Skip'],
                        ].map(([decision, label]) => (
                          <button
                            disabled={busy}
                            key={decision}
                            onClick={() =>
                              void action(() =>
                                api(`/admin/email/recipients/${r.id}/resolve`, {
                                  method: 'POST',
                                  body: JSON.stringify({ decision }),
                                }),
                              )
                            }
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    )}
                  </article>
                ))}
              </div>
              <button disabled={!page} onClick={() => setPage(page - 1)}>
                Previous 50
              </button>
              <button
                disabled={detail.data.recipients.length < 50}
                onClick={() => setPage(page + 1)}
              >
                Next 50
              </button>
            </div>
          </section>
        )}
      </div>
    );
  return (
    <div className="email-system">
      <header className="admin-heading">
        <p>Communications</p>
        <h1>Email System</h1>
        <span>Service updates, saved campaigns, and delivery progress.</span>
      </header>
      {notice && (
        <p className="settings-notice" role="status">
          {notice}
        </p>
      )}
      <div className="email-allowance">
        <strong>{(overview.data?.used ?? 0).toLocaleString()} / 50,000</strong>
        <span>
          Reserved attempts in the last 24 hours, across all emails. Campaigns stop at 45,000 to
          reserve capacity for account emails. Global ceiling: 14 per second.
        </span>
      </div>
      <nav className="email-tabs" aria-label="Email System">
        {[
          ['compose', 'Compose'],
          ['history', 'Campaign history'],
          ['deletions', 'Account deletions'],
        ].map(([id, label]) => (
          <button
            key={id}
            aria-current={view === id ? 'page' : undefined}
            onClick={() => setView(id as typeof view)}
          >
            {label}
          </button>
        ))}
      </nav>
      <section className="email-compose" hidden={view !== 'compose'}>
        <h2>Compose an update</h2>
        <EmailSelect
          label="Template"
          value={draft?.id ?? ''}
          options={[
            { value: '', label: 'Choose a template' },
            ...(templates.data?.templates.map((t) => ({ value: t.id, label: t.subject })) ?? []),
          ]}
          onChange={(value) => {
            const t = templates.data?.templates.find((t) => t.id === value);
            if (t) {
              setRequestId(crypto.randomUUID());
              setDraft({ ...t });
              setConfirm(false);
            }
          }}
        />
        {templates.isError && (
          <p role="alert">
            Templates could not be loaded.{' '}
            <button onClick={() => void templates.refetch()}>Retry</button>
          </p>
        )}
        {draft && (
          <>
            <label>
              Subject
              <input
                maxLength={200}
                value={draft.subject}
                onChange={(e) => {
                  setDraft({ ...draft, subject: e.target.value });
                  setConfirm(false);
                }}
              />
            </label>
            <p className="email-hint">
              Edit the full HTML and the plain-text fallback. Include the effective date and summary
              of changes before sending. These templates do not set a notice period for you.
            </p>
            <div className="email-editor">
              <label>
                HTML source
                <textarea
                  spellCheck={false}
                  value={draft.html}
                  onChange={(e) => {
                    setDraft({ ...draft, html: e.target.value });
                    setConfirm(false);
                  }}
                />
              </label>
              <div>
                <span>Recipient preview</span>
                <Preview html={draft.html} />
              </div>
            </div>
            <label>
              Plain-text version
              <textarea
                rows={5}
                value={draft.text}
                onChange={(e) => {
                  setDraft({ ...draft, text: e.target.value });
                  setConfirm(false);
                }}
              />
            </label>
            <button
              className="button button--dash-quiet"
              disabled={
                busy ||
                !user?.email ||
                !draft.subject.trim() ||
                !draft.html.trim() ||
                !draft.text.trim()
              }
              onClick={() =>
                void action(async () => {
                  const r = await api<{ id: string }>('/admin/email/campaigns', {
                    method: 'POST',
                    body: JSON.stringify({
                      ...draft,
                      subject: `[Test] ${draft.subject}`.slice(0, 200),
                      requestId: crypto.randomUUID(),
                      template: draft.id,
                      filters: { ...initial, emails: [user!.email] },
                      confirmedCount: 1,
                    }),
                  });
                  setSelected(r.id);
                  setPage(0);
                  setHistoryPage(0);
                  setNotice(`Test queued for ${user!.email}.`);
                })
              }
            >
              Send a test to my account
            </button>
            <fieldset>
              <legend>Recipients</legend>
              <p className="email-hint">
                Selected account groups are combined. All other filters narrow that group. Only
                active accounts are eligible; deleted and suspended accounts are excluded.
              </p>
              <div className="email-checks">
                {[
                  ['paid', 'Active paid subscriptions'],
                  ['free', 'Free accounts'],
                  ['other', 'Other active accounts (trials, gifted plans, unpaid)'],
                ].map(([value, label]) => (
                  <label key={value}>
                    <input
                      type="checkbox"
                      checked={filters.groups.includes(value!)}
                      onChange={(e) =>
                        change({
                          groups: e.target.checked
                            ? [...filters.groups, value!]
                            : filters.groups.filter((g) => g !== value),
                        })
                      }
                    />
                    {label}
                  </label>
                ))}
              </div>
              <div className="email-filters">
                <EmailSelect
                  label="Account created"
                  value={filters.ageDirection}
                  options={[
                    { value: 'newer', label: 'Less than' },
                    { value: 'older', label: 'More than' },
                  ]}
                  onChange={(value) => change({ ageDirection: value })}
                />
                <label>
                  Age (blank = any)
                  <input
                    type="number"
                    min={1}
                    max={10000}
                    value={filters.age ?? ''}
                    onChange={(e) =>
                      change({ age: e.target.value ? Number(e.target.value) : null })
                    }
                  />
                </label>
                <EmailSelect
                  label="Unit"
                  value={filters.ageUnit}
                  options={['days', 'months', 'years'].map((value) => ({ value, label: value }))}
                  onChange={(value) => change({ ageUnit: value })}
                />
                <EmailSelect
                  label="Affiliate status"
                  value={filters.affiliate}
                  options={[
                    { value: 'all', label: 'All accounts' },
                    { value: 'yes', label: 'Active affiliates only' },
                    { value: 'no', label: 'Exclude active affiliates' },
                  ]}
                  onChange={(value) => change({ affiliate: value })}
                />
              </div>
              <div className="email-filters">
                <label>
                  Created on or after
                  <input
                    type="date"
                    value={filters.createdAfter ?? ''}
                    onChange={(e) => change({ createdAfter: e.target.value || null })}
                  />
                </label>
                <label>
                  Created on or before
                  <input
                    type="date"
                    value={filters.createdBefore ?? ''}
                    onChange={(e) => change({ createdBefore: e.target.value || null })}
                  />
                </label>
                <EmailSelect
                  label="Last sign-in"
                  value={filters.activity}
                  options={[
                    { value: 'any', label: 'Any activity' },
                    { value: 'never', label: 'Never signed in' },
                    { value: 'recent', label: 'Within the last X days' },
                    { value: 'inactive', label: 'Inactive for X days' },
                  ]}
                  onChange={(value) => change({ activity: value })}
                />
                <label>
                  Activity window (days)
                  <input
                    type="number"
                    min={1}
                    max={3650}
                    disabled={!['recent', 'inactive'].includes(filters.activity)}
                    value={filters.activityDays}
                    onChange={(e) => change({ activityDays: Number(e.target.value) })}
                  />
                </label>
              </div>
              <div className="email-plan-picker">
                <span>Plans (none selected = all plans)</span>
                <div className="email-checks">
                  {templates.data?.plans.map((plan) => (
                    <label key={plan.code}>
                      <input
                        type="checkbox"
                        checked={filters.plans.includes(plan.code)}
                        onChange={(e) =>
                          change({
                            plans: e.target.checked
                              ? [...filters.plans, plan.code]
                              : filters.plans.filter((code) => code !== plan.code),
                          })
                        }
                      />
                      {plan.name}
                    </label>
                  ))}
                </div>
              </div>
              <label>
                Only these email addresses (optional, one per line)
                <textarea
                  rows={3}
                  value={filters.emails.join('\n')}
                  onChange={(e) =>
                    change({ emails: e.target.value ? e.target.value.split('\n') : [] })
                  }
                  onBlur={() =>
                    change({ emails: filters.emails.map((v) => v.trim()).filter(Boolean) })
                  }
                />
              </label>
              <label>
                Exclude these email addresses (optional)
                <textarea
                  rows={3}
                  value={filters.excludeEmails.join('\n')}
                  onChange={(e) =>
                    change({ excludeEmails: e.target.value ? e.target.value.split('\n') : [] })
                  }
                  onBlur={() =>
                    change({
                      excludeEmails: filters.excludeEmails.map((v) => v.trim()).filter(Boolean),
                    })
                  }
                />
              </label>
              <button
                className="button button--dash-quiet"
                disabled={busy || !filters.groups.length}
                onClick={() =>
                  void action(async () => {
                    setAudience(
                      await api('/admin/email/audience', {
                        method: 'POST',
                        body: JSON.stringify(filters),
                      }),
                    );
                  })
                }
              >
                Preview recipients
              </button>
              {audience && (
                <div className="email-audience">
                  <strong>{audience.total.toLocaleString()} recipients</strong>
                  <p>Sample of up to 20 accounts:</p>
                  <ul>
                    {audience.sample.map((u) => (
                      <li key={u.id}>{u.email}</li>
                    ))}
                  </ul>
                </div>
              )}
            </fieldset>
            {confirm ? (
              <div className="email-confirm" role="alert">
                <p>
                  Queue “{draft.subject}” for {audience?.total} accounts? The content and recipient
                  list will be saved as a snapshot. Sending starts immediately.
                </p>
                <button
                  className="button button--dash-primary"
                  disabled={busy}
                  onClick={() =>
                    void action(async () => {
                      const r = await api<{ id: string }>('/admin/email/campaigns', {
                        method: 'POST',
                        body: JSON.stringify({
                          ...draft,
                          requestId,
                          template: draft.id,
                          filters,
                          confirmedCount: audience?.total,
                        }),
                      });
                      setSelected(r.id);
                      setPage(0);
                      setHistoryPage(0);
                      setDraft(null);
                      setConfirm(false);
                      setAudience(null);
                      setNotice('Campaign queued.');
                    })
                  }
                >
                  Confirm and queue
                </button>
                <button
                  className="button button--dash-quiet"
                  disabled={busy}
                  onClick={() => setConfirm(false)}
                >
                  Back to editing
                </button>
              </div>
            ) : (
              <button
                className="button button--dash-primary"
                disabled={
                  busy ||
                  !audience?.total ||
                  !draft.subject.trim() ||
                  !draft.html.trim() ||
                  !draft.text.trim()
                }
                onClick={() => setConfirm(true)}
              >
                Review and send
              </button>
            )}
          </>
        )}
      </section>
      <section hidden={view !== 'history'}>
        <h2>Campaign history</h2>
        <p className="email-hint">
          Sent means accepted by the email provider, not confirmed inbox delivery. Temporary
          failures retry after one hour. Uncertain submissions need review against provider logs.
        </p>
        {overview.isError && <p role="alert">Unable to load campaigns.</p>}
        {!overview.data?.campaigns.length && <p>No campaigns yet.</p>}
        <div className="email-history">
          {overview.data?.campaigns.map((c) => (
            <article key={c.id} className="email-campaign-row">
              <button
                className="email-history__title"
                onClick={() => {
                  setSelected(c.id);
                  setPage(0);
                }}
              >
                {c.subject}
              </button>
              <span>
                <b className={`email-status email-status--${c.status}`}>{c.status}</b> {c.sent} /{' '}
                {c.total} sent · {c.skipped} skipped
                {c.uncertain > 0 ? ` · ${c.uncertain} need review` : null}
              </span>
              <progress
                aria-label={`Delivery progress for ${c.subject}`}
                value={c.sent + c.skipped}
                max={Math.max(1, c.total)}
              />
              {c.status === 'running' && c.next_attempt_at && (
                <small>Next attempt: {time(c.next_attempt_at)}</small>
              )}
              {['running', 'paused'].includes(c.status) && (
                <div>
                  <button
                    disabled={busy}
                    onClick={() =>
                      void action(() =>
                        api(`/admin/email/campaigns/${c.id}/state`, {
                          method: 'POST',
                          body: JSON.stringify({
                            status: c.status === 'paused' ? 'running' : 'paused',
                          }),
                        }),
                      )
                    }
                  >
                    {c.status === 'paused' ? 'Resume' : 'Pause'}
                  </button>
                  <button
                    disabled={busy}
                    onClick={() =>
                      void action(() =>
                        api(`/admin/email/campaigns/${c.id}/state`, {
                          method: 'POST',
                          body: JSON.stringify({ status: 'cancelled' }),
                        }),
                      )
                    }
                  >
                    Cancel remaining
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
        <div className="email-actions">
          <button disabled={!historyPage} onClick={() => setHistoryPage(historyPage - 1)}>
            Newer campaigns
          </button>
          <span>Page {historyPage + 1}</span>
          <button
            disabled={(historyPage + 1) * 25 >= (overview.data?.campaignCount ?? 0)}
            onClick={() => setHistoryPage(historyPage + 1)}
          >
            Older campaigns
          </button>
        </div>
      </section>
      {detail.isError && <p role="alert">The campaign details could not be loaded.</p>}
      <section hidden={view !== 'deletions'}>
        <h2>Account deletion jobs</h2>
        <div className="email-deliveries">
          {overview.data?.deletions.map((d) => (
            <article key={d.user_id}>
              <strong>{d.user_id}</strong>
              <span>
                {d.status} · {d.attempts} retries
              </span>
              <small>
                {d.completed_at
                  ? `Completed: ${time(d.completed_at)}`
                  : `Next attempt: ${time(d.next_attempt_at)}`}
              </small>
              {d.last_error && <p>{d.last_error}</p>}
              {d.status === 'uncertain' && (
                <div className="email-actions">
                  <button
                    disabled={busy}
                    onClick={() =>
                      void action(() =>
                        api(`/admin/email/deletions/${d.user_id}/resolve`, {
                          method: 'POST',
                          body: JSON.stringify({ decision: 'sent' }),
                        }),
                      )
                    }
                  >
                    Confirm provider accepted
                  </button>
                  <button
                    disabled={busy}
                    onClick={() =>
                      void action(() =>
                        api(`/admin/email/deletions/${d.user_id}/resolve`, {
                          method: 'POST',
                          body: JSON.stringify({ decision: 'retry' }),
                        }),
                      )
                    }
                  >
                    Retry final email (may duplicate)
                  </button>
                </div>
              )}
              {['pending', 'email_pending'].includes(d.status) && (
                <button
                  disabled={busy}
                  onClick={() =>
                    void action(() =>
                      api(`/admin/email/deletions/${d.user_id}/retry`, { method: 'POST' }),
                    )
                  }
                >
                  Retry now
                </button>
              )}
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
